import { useEffect, useState } from 'react';
import { supabase } from './supabase';

// ============================================================
// PINTAR UNA FOTO QUE YA NO ES PÚBLICA (mig 211)
// ============================================================
// Los avatares salieron de `vertex-assets` —público, sin condición de ruta— a
// `vertex-avatars`, privado. La razón no fue que el uuid se pudiera adivinar,
// sino que la URL pública era **una llave permanente que ninguna revocación
// cancela**: quien la tuviera seguía viendo la foto de un menor después de
// dejar el club.
//
// `profiles.avatar_url` guarda ahora la RUTA (`avatars/{id}`), no una URL: una
// URL firmada caduca y guardarla sería guardar basura con fecha.
//
// El panel SOLO LEE. Dar de alta la foto de alguien es de la app —uno mismo o
// su tutor—; dirección puede BORRAR una foto inapropiada, pero no poner una.
//
// Gemelo de `app/src/services/avatar.ts`. No se comparte fichero a propósito:
// cada módulo tiene su cliente de Supabase y sus tipos generados.

const AVATAR_BUCKET = 'vertex-avatars';

/** Mismo criterio que las URL prefirmadas del GYM (ADR-0014): una hora. */
const TTL_SEGUNDOS = 3600;
const RENOVAR_MS = (TTL_SEGUNDOS - 300) * 1000;

const permisos = new Map<string, { url: string; caducaEn: number }>();

/**
 * Convierte lo que hay en `profiles.avatar_url` en algo pintable.
 *
 * Tolera los dos formatos a propósito: un valor que empieza por `http` es de
 * antes de la mig 211 y se usa tal cual. El panel se despliega solo al mergear
 * (Vercel) y la migración necesita autorización para aplicarse, así que los dos
 * momentos NO van sincronizados y el código tiene que aguantar el desfase en
 * cualquier orden.
 */
export async function signAvatar(value: string | null | undefined): Promise<string | null> {
  if (!value) return null;
  if (value.startsWith('http')) return value;

  const guardado = permisos.get(value);
  if (guardado && guardado.caducaEn > Date.now()) return guardado.url;

  const { data, error } = await supabase.storage
    .from(AVATAR_BUCKET)
    .createSignedUrl(value, TTL_SEGUNDOS);
  // Sin permiso no hay foto, y no es un error que enseñar: la cabecera ya sabe
  // pintar a alguien sin foto. Pasa mientras el objeto no se ha trasladado.
  if (error || !data?.signedUrl) return null;

  permisos.set(value, { url: data.signedUrl, caducaEn: Date.now() + RENOVAR_MS });
  return data.signedUrl;
}

/**
 * La URL pintable de un avatar, renovada antes de caducar.
 *
 * La renovación no sobra: la cabecera del panel está en pantalla toda la
 * jornada, y sin ella la foto desaparecería a la hora.
 */
export function useAvatarUri(value: string | null | undefined): string | null {
  const [uri, setUri] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    let temporizador: ReturnType<typeof setTimeout> | undefined;

    const resolver = async () => {
      const u = await signAvatar(value);
      if (!vivo) return;
      setUri(u);
      if (u && value && !value.startsWith('http')) {
        temporizador = setTimeout(resolver, RENOVAR_MS);
      }
    };

    setUri(null);
    void resolver();

    return () => {
      vivo = false;
      if (temporizador) clearTimeout(temporizador);
    };
  }, [value]);

  return uri;
}
